import { useState } from "react";
import { toast } from "sonner";
import { PackageSearch } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { MaskedLines, FadeUp } from "../components/Reveal";

const inputCls = "w-full bg-transparent border border-[#27272A] px-4 py-3.5 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30";

export default function SellToUs() {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", stock_description: "", est_units: "", asking_price: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sell-to-us", {
        ...form,
        est_units: form.est_units ? parseInt(form.est_units) : null,
        asking_price: form.asking_price ? parseFloat(form.asking_price) : null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-2 gap-16">
      <div>
        <MaskedLines lines={["GOT STOCK?", "SELL IT", "TO US."] } className="font-display text-6xl sm:text-7xl leading-[0.9]" />
        <p className="text-white/50 text-sm sm:text-base mt-8 max-w-md leading-relaxed">
          Retail returns, end-of-line, overstock, full pallets or trailer loads. We buy outright or take consignment. Tell us what you've got — we review every submission within 48 hours.
        </p>
        <div className="mt-10 space-y-4">
          {["Outright purchase or consignment split", "Nationwide pallet collection arranged", "Fast decisions, faster payment", "Any category, any grade — be honest about condition"].map((t, i) => (
            <div key={i} className="flex items-center gap-4 text-sm text-white/60">
              <span className="text-[#FFCC00] font-display">{String(i + 1).padStart(2, "0")}</span> {t}
            </div>
          ))}
        </div>
      </div>
      <div className="border border-[#27272A] p-8 sm:p-12 bg-[#111111] h-fit">
        <div className="hazard-stripes h-1.5 -mt-8 sm:-mt-12 -mx-8 sm:-mx-12 mb-8" />
        {done ? (
          <div className="text-center py-12" data-testid="sell-to-us-success">
            <PackageSearch size={48} className="text-[#FFCC00] mx-auto mb-6" />
            <h2 className="font-display text-3xl mb-3">SUBMISSION RECEIVED.</h2>
            <p className="text-white/50 text-sm">Our buying team reviews every lot within 48 hours. If it's a fit, we'll call you.</p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-3xl mb-8">SUBMIT YOUR STOCK</h2>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="sell-company-input" required placeholder="COMPANY" value={form.company} onChange={set("company")} className={inputCls} />
                <input data-testid="sell-name-input" required placeholder="CONTACT NAME" value={form.contact_name} onChange={set("contact_name")} className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="sell-email-input" required type="email" placeholder="EMAIL" value={form.email} onChange={set("email")} className={inputCls} />
                <input data-testid="sell-phone-input" required placeholder="PHONE" value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <textarea data-testid="sell-description-input" required rows={4} placeholder="WHAT'S ON THE PALLET? BRANDS, CONDITION, LOCATION…" value={form.stock_description} onChange={set("stock_description")} className={inputCls} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="sell-units-input" type="number" min="1" placeholder="EST. UNITS (OPTIONAL)" value={form.est_units} onChange={set("est_units")} className={inputCls} />
                <input data-testid="sell-price-input" type="number" min="0" step="0.01" placeholder="ASKING PRICE £ (OPTIONAL)" value={form.asking_price} onChange={set("asking_price")} className={inputCls} />
              </div>
              <button data-testid="sell-submit-button" disabled={busy} className="btn-industrial w-full py-4 text-xl">
                {busy ? "SENDING…" : "SUBMIT FOR REVIEW"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
